import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { env } from "../config/env";
import { get } from "./storage";
import { attachSessionRefresh } from "../features/auth/services/sessionService";

const STORAGE_KEYS = {
  ACCESS_TOKEN: "ACCESS_TOKEN",
  USER_ID: "USER_ID",
} as const;

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: env.REACT_APP_API_URL,
      withCredentials: true,
    });

    // Request interceptor: attach auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = get(STORAGE_KEYS.ACCESS_TOKEN);
        if (token && config.headers) {
          config.headers.Authorization = token;
          config.headers["Content-Type"] = "application/json; charset=utf-8";
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Expired tokens: silent refresh + retry, else end the session.
    attachSessionRefresh(this.client);
  }

  private unwrapResponse<T>(response: AxiosResponse<any>): T {
    // Handle new API envelope: { success, code, message, data }
    const body = response.data;
    if (body && typeof body === "object" && "success" in body && "data" in body) {
      return body.data as T;
    }
    // Fallback for responses without the envelope
    return body as T;
  }

  async get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get(path, config);
    return this.unwrapResponse<T>(response);
  }

  async post<T>(
    path: string,
    data: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.post(path, data, config);
    return this.unwrapResponse<T>(response);
  }

  async patch<T>(
    path: string,
    data: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.patch(path, data, config);
    return this.unwrapResponse<T>(response);
  }

  async delete<T>(
    path: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.delete(path, config);
    return this.unwrapResponse<T>(response);
  }
}

export const apiClient = new ApiClient();

/**
 * The `message` string out of a failed `{ success, code, message, data }`
 * envelope. The variant endpoints return specific, user-meaningful messages
 * ("package_name is already used by another variant: …") that are worth showing
 * verbatim rather than replacing with a generic failure.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === "string" && message ? message : fallback;
}

/** HTTP status of a failed request, when there was a response at all. */
export function getApiErrorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

export { STORAGE_KEYS };
